/**
 * The ONE source of the app's responsive breakpoints for JAVASCRIPT.
 *
 * ⚠️ WHY THIS EXISTS (item 1, operator-ruled 2026-08-14). Tailwind v4's default
 * breakpoints are expressed in **rem**, so the stylesheet switches at a multiple
 * of the visitor's ROOT FONT SIZE. Verified in the shipped stylesheet, not assumed:
 * `dist/assets/app-*.css` emits `@media(min-width:64rem)` for `lg`, `48rem` for
 * `md`. Every JS comparison in this app instead counted CSS **pixels**
 * (`window.innerWidth >= 1024`, `matchMedia('(min-width: 768px)')`).
 *
 * At the default 16px root the two agree, which is why this survived so long. For
 * a visitor who has RAISED their default font size they diverge — at a 20px root,
 * `lg` becomes 1280px while the code still believes 1024px. In that band the
 * stylesheet shows the mobile hamburger while the code believes it is on desktop.
 * The worst instance: Header's resize handler CLOSED the mobile drawer on any
 * resize, leaving a visitor with the hamburger hidden-by-code and no menu bar —
 * i.e. no navigation at all. Page zoom is unaffected; this reaches only people who
 * changed their default font size, which is an accessibility need already.
 *
 * ⚠️ ASK THE BROWSER, DO NOT CONVERT. These helpers build a `rem` media query and
 * hand it to `matchMedia`, so the browser evaluates the SAME question the
 * stylesheet asks. Do not "simplify" this by multiplying rem by a root font size
 * read from the DOM: that reintroduces a second opinion about the breakpoint,
 * which is the entire defect.
 */

/**
 * Tailwind v4 default breakpoints, in rem.
 *
 * ⚠️ These are DEFAULTS: `tailwind.config.js` deliberately does NOT override
 * `screens` (verified — the config has no `screens` key). If a future change adds
 * one, these values must move with it, and this comment is the reason a reader
 * should check rather than assume.
 */
export const BREAKPOINT_REM = {
    sm: 40,
    md: 48,
    lg: 64,
    xl: 80,
    '2xl': 96,
} as const;

export type Breakpoint = keyof typeof BREAKPOINT_REM;

/** The media query for "at or above this breakpoint" — in rem, as the stylesheet is. */
export const breakpointQuery = (bp: Breakpoint): string =>
    `(min-width: ${BREAKPOINT_REM[bp]}rem)`;

/**
 * The viewport width assumed during SSG prerendering and the hydration render.
 *
 * ⚠️ MUST MATCH `hooks/useWindowSize.ts`'s hard-coded SSR width. That hook returns
 * `{ width: 1200 }` on the server AND on the first client render so the hydrated
 * DOM matches the prerendered HTML exactly (React hydration error #418). Anything
 * derived from a breakpoint has to make the SAME first-render decision or it
 * reintroduces that mismatch on all 4,202 prerendered pages. Change both together.
 */
export const SSR_VIEWPORT_WIDTH_PX = 1200;

/** The root font size browsers use unless the visitor changes it. */
const NOMINAL_ROOT_PX = 16;

/**
 * What a breakpoint evaluates to during prerender/hydration, DERIVED from
 * SSR_VIEWPORT_WIDTH_PX rather than hand-listed, so the two cannot drift.
 * At 1200px / 16px root: sm, md, lg are true; xl and 2xl are false.
 */
export const matchesBreakpointAtSsr = (bp: Breakpoint): boolean =>
    SSR_VIEWPORT_WIDTH_PX >= BREAKPOINT_REM[bp] * NOMINAL_ROOT_PX;

/**
 * Imperative read, for event handlers and other non-render code.
 *
 * Returns the SSR answer when there is no `window` (prerender) or no `matchMedia`
 * (old jsdom), so a caller never has to special-case it.
 */
export const matchesBreakpointUp = (bp: Breakpoint): boolean => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return matchesBreakpointAtSsr(bp);
    }
    return window.matchMedia(breakpointQuery(bp)).matches;
};
